import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import {
  Loader2,
  Sparkles,
  Send,
  Eye,
  Save,
  X,
  Mail,
  CalendarClock,
  Link as LinkIcon,
} from "lucide-react";
import { API, buildHeaders, TOKENS, renderTokens } from "./shared";

// ─────────────────────────────────────────────────────────────────────
// Full Preview modal
// ─────────────────────────────────────────────────────────────────────

function FullPreviewModal({ open, onClose, previews, waveName }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => { if (open) setIdx(0); }, [open]);
  if (!open) return null;
  const p = previews[idx];
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/55 p-3 sm:p-4" data-testid="full-preview-modal">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 flex items-center justify-between" style={{ background: "#0b1220", color: "#fff" }}>
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#C79A3B]">Full preview</div>
            <div className="text-[15px] font-bold truncate">{waveName} — {previews.length} recipients</div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-md hover:bg-white/10 text-white/70 hover:text-white" data-testid="preview-close-btn">
            <X className="h-4 w-4" />
          </button>
        </div>
        {p ? (
          <div className="px-5 py-4 overflow-y-auto flex-1">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <button type="button" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0}
                className="px-3 h-8 rounded-md border border-[#e5e7eb] text-[12px] font-semibold disabled:opacity-40" data-testid="preview-prev">
                ← Prev
              </button>
              <div className="text-[12px] text-[#475569]">
                <strong>{idx + 1}</strong> of {previews.length} · {p.school_name}
              </div>
              <button type="button" onClick={() => setIdx(Math.min(previews.length - 1, idx + 1))} disabled={idx === previews.length - 1}
                className="px-3 h-8 rounded-md border border-[#e5e7eb] text-[12px] font-semibold disabled:opacity-40" data-testid="preview-next">
                Next →
              </button>
            </div>
            <div className="rounded-xl border border-[#e5e7eb] p-4 bg-[#fafafa]">
              <div className="text-[11px] font-bold tracking-[0.10em] uppercase text-[#94a3b8]">To</div>
              <div className="text-[13.5px] font-semibold text-[#0f172a]">{p.leader_name} — {p.leader_role}</div>
              <div className="mt-3 text-[11px] font-bold tracking-[0.10em] uppercase text-[#94a3b8]">Subject</div>
              <div className="text-[14px] font-bold text-[#0f172a]">{p.subject}</div>
              <div className="mt-3 text-[11px] font-bold tracking-[0.10em] uppercase text-[#94a3b8]">Body</div>
              <pre className="text-[13px] text-[#0f172a] whitespace-pre-wrap font-sans">{p.body}</pre>
            </div>
          </div>
        ) : (
          <div className="px-5 py-10 text-center text-[#94a3b8]">No previews available.</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Compose tab
// ─────────────────────────────────────────────────────────────────────

export default function ComposeTab({ session, campaign }) {
  const [waves, setWaves] = useState([]);
  const [activeWaveId, setActiveWaveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const headers = useMemo(() => buildHeaders(session), [session]);

  const load = useCallback(async () => {
    if (!campaign?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/campaigns/${campaign.id}/waves`, { headers });
      setWaves(r.data || []);
      if (!activeWaveId && r.data?.length) setActiveWaveId(r.data[0].id);
    } catch { toast.error("Failed to load waves"); }
    finally { setLoading(false); }
  }, [campaign?.id, headers, activeWaveId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setActiveWaveId(null); }, [campaign?.id]);

  const wave = waves.find((w) => w.id === activeWaveId);

  const updateField = (field, value) => {
    setWaves((prev) => prev.map((w) => (w.id === activeWaveId ? { ...w, [field]: value } : w)));
  };

  const saveWave = async () => {
    if (!wave) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/api/admin/campaigns/${campaign.id}/waves/${wave.id}`,
        { name: wave.name, subject: wave.subject, body: wave.body, calendly_url: wave.calendly_url, scheduled_at: wave.scheduled_at },
        { headers },
      );
      toast.success("Wave saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const sendWave = async () => {
    if (!wave) return;
    if (!window.confirm(`Send Wave ${wave.wave_number}? Phase 2 keeps sends MOCKED — no real email leaves but recipient statuses will move to 'sent'.`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/admin/campaigns/${campaign.id}/waves/${wave.id}/send`, {}, { headers });
      toast.success(`Wave ${wave.wave_number} sent (MOCKED) — ${r.data.sent_count} recipients`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const openPreview = async () => {
    if (!wave) return;
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const r = await axios.post(`${API}/api/admin/campaigns/${campaign.id}/waves/${wave.id}/preview`, {}, { headers });
      setPreviews(r.data.previews || []);
    } catch { toast.error("Preview failed"); setPreviewOpen(false); }
    finally { setPreviewLoading(false); }
  };

  const insertToken = (tokenKey) => {
    const ta = document.getElementById(`compose-body-${activeWaveId}`);
    if (!ta) return updateField("body", (wave.body || "") + tokenKey);
    const start = ta.selectionStart || 0;
    const end = ta.selectionEnd || 0;
    const next = (wave.body || "").slice(0, start) + tokenKey + (wave.body || "").slice(end);
    updateField("body", next);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + tokenKey.length, start + tokenKey.length);
    }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-14" data-testid="compose-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#94a3b8]" />
      </div>
    );
  }

  return (
    <div data-testid="compose-tab" className="space-y-4">
      {/* Wave selector */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="compose-wave-selector">
        {waves.map((w) => {
          const active = w.id === activeWaveId;
          return (
            <button
              key={w.id}
              type="button"
              onClick={() => setActiveWaveId(w.id)}
              className="inline-flex items-center gap-2 px-3 h-9 rounded-md text-[12.5px] font-semibold border transition-colors"
              style={{
                background: active ? "#0f172a" : "#fff",
                color: active ? "#fff" : "#475569",
                borderColor: active ? "#0f172a" : "#e5e7eb",
              }}
              data-testid={`compose-wave-pill-${w.wave_number}`}
            >
              Wave {w.wave_number}
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold"
                style={{ background: w.status === "sent" ? "#dcfce7" : "#f1f5f9", color: w.status === "sent" ? "#15803d" : "#475569" }}
              >
                {w.status}
              </span>
            </button>
          );
        })}
      </div>

      {wave ? (
        <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#e5e7eb" }}>
          {/* Name + Subject */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <div className="text-[11px] font-bold tracking-[0.08em] uppercase text-[#475569] mb-1">Wave name</div>
              <input
                value={wave.name || ""}
                onChange={(e) => updateField("name", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-[#e5e7eb] bg-white text-[13px] text-[#0f172a]"
                data-testid="compose-name-input"
              />
            </label>
            <label className="block">
              <div className="text-[11px] font-bold tracking-[0.08em] uppercase text-[#475569] mb-1">Subject</div>
              <input
                value={wave.subject || ""}
                onChange={(e) => updateField("subject", e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-[#e5e7eb] bg-white text-[13px] text-[#0f172a]"
                placeholder="e.g., Action item for {{school_name}} ahead of {{deadline}}"
                data-testid="compose-subject-input"
              />
            </label>
          </div>

          {/* Token chips */}
          <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid="compose-tokens-row">
            <div className="text-[11px] font-bold tracking-[0.08em] uppercase text-[#475569] mr-1">Insert token:</div>
            {TOKENS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => insertToken(t.key)}
                className="px-2 h-7 rounded-full text-[11px] font-semibold bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#475569]"
                title={t.label}
                data-testid={`compose-token-${t.key.replace(/[{}]/g, "")}`}
              >
                {t.key}
              </button>
            ))}
          </div>

          {/* Body */}
          <label className="block mt-4">
            <div className="text-[11px] font-bold tracking-[0.08em] uppercase text-[#475569] mb-1">Body</div>
            <textarea
              id={`compose-body-${wave.id}`}
              value={wave.body || ""}
              onChange={(e) => updateField("body", e.target.value)}
              rows={9}
              className="w-full px-3 py-2 rounded-md border border-[#e5e7eb] bg-white text-[13px] text-[#0f172a] font-sans"
              placeholder={"Hi {{leader_name}},\n\nQuick note about {{campaign_name}} for {{school_name}}…"}
              data-testid="compose-body-textarea"
            />
          </label>

          {/* Calendly + Schedule */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <label className="block">
              <div className="text-[11px] font-bold tracking-[0.08em] uppercase text-[#475569] mb-1 flex items-center gap-1.5">
                <LinkIcon className="h-3 w-3" /> Calendly URL
              </div>
              <input
                value={wave.calendly_url || ""}
                onChange={(e) => updateField("calendly_url", e.target.value)}
                placeholder="https://calendly.com/your-team/30min"
                className="w-full h-10 px-3 rounded-md border border-[#e5e7eb] bg-white text-[13px] text-[#0f172a]"
                data-testid="compose-calendly-input"
              />
              <div className="mt-1 text-[11px] text-[#94a3b8]">Available as <code>{"{{calendly_url}}"}</code> token.</div>
            </label>
            <label className="block">
              <div className="text-[11px] font-bold tracking-[0.08em] uppercase text-[#475569] mb-1 flex items-center gap-1.5">
                <CalendarClock className="h-3 w-3" /> Scheduled at (optional)
              </div>
              <input
                type="datetime-local"
                value={wave.scheduled_at ? String(wave.scheduled_at).slice(0, 16) : ""}
                onChange={(e) => updateField("scheduled_at", e.target.value ? new Date(e.target.value).toISOString() : null)}
                className="w-full h-10 px-3 rounded-md border border-[#e5e7eb] bg-white text-[13px] text-[#0f172a]"
                data-testid="compose-schedule-input"
              />
            </label>
          </div>

          {/* Actions */}
          <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={openPreview}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#e5e7eb] bg-white text-[12.5px] font-semibold text-[#475569] hover:bg-[#f8fafc]"
              data-testid="compose-preview-btn"
            >
              <Eye className="h-3.5 w-3.5" /> Full Preview
            </button>
            <button
              type="button"
              onClick={saveWave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-[#e5e7eb] bg-white text-[12.5px] font-semibold text-[#3B4FE4] hover:bg-[#eef2ff] disabled:opacity-50"
              data-testid="compose-save-btn"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save
            </button>
            <button
              type="button"
              onClick={sendWave}
              disabled={sending || wave.status === "sent"}
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md text-[12.5px] font-semibold text-white shadow-[0_4px_12px_rgba(199,154,59,0.35)] hover:opacity-95 disabled:opacity-50"
              style={{ background: wave.status === "sent" ? "#94a3b8" : "#C79A3B" }}
              data-testid="compose-send-btn"
            >
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {wave.status === "sent" ? "Already Sent" : "Send Wave (Mocked)"}
            </button>
          </div>

          {/* Footer notice */}
          <div className="mt-3 rounded-md px-3 py-2 text-[11.5px] flex items-center gap-1.5"
            style={{ background: "rgba(199,154,59,0.10)", color: "#92400e" }}>
            <Sparkles className="h-3 w-3" />
            Sends are MOCKED in Phase 2 — no real email is delivered. Each "send" records mocked stats and moves
            queued schools to <strong>booked</strong>.
          </div>
        </div>
      ) : (
        <div className="text-center py-10 text-[#94a3b8]" data-testid="compose-no-waves">
          No waves configured. Use the Waves tab to add one.
        </div>
      )}

      <FullPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        previews={previewLoading ? [] : previews}
        waveName={wave?.name || ""}
      />
    </div>
  );
}
